import { z } from 'zod';
export * from './agent-configuration';

export const guardrailCategories = [
  'TOPIC_BOUNDARIES',
  'CLAIMS_AND_COMMITMENTS',
  'DATA_AND_PRIVACY',
  'TRANSACTION_AND_ACTION',
  'REGULATORY_COMPLIANCE',
  'SAFETY_AND_WELLBEING',
  'CONTENT_AND_CONDUCT',
  'OPERATIONAL_BOUNDARIES',
  'HANDOVER_AND_ESCALATION',
] as const;

export const guardrailSeverities = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'] as const;
export const enforcementTypes = [
  'BLOCK',
  'REDIRECT',
  'REQUIRE_CONFIRMATION',
  'REQUIRE_AUTHENTICATION',
  'ESCALATE',
  'WARN_INTERNAL',
  'MONITOR',
] as const;
export const configurationStatuses = [
  'DRAFT',
  'AWAITING_APPROVAL',
  'APPROVED',
  'SCHEDULED',
  'PUBLISHED',
  'EXPIRED',
  'REJECTED',
] as const;
export const inheritanceStates = ['INHERITED', 'OVERRIDDEN', 'LOCAL'] as const;

export const scopeSchema = z.object({
  platform: z.string().min(1),
  useCase: z.string().min(1),
  market: z.string().length(2),
  channel: z.string().min(1),
  language: z.string().min(2),
  environment: z.enum(['DEVELOPMENT', 'STAGING', 'PRODUCTION']),
});

export const guardrailSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  objective: z.string(),
  category: z.enum(guardrailCategories),
  severity: z.enum(guardrailSeverities),
  enforcement: z.enum(enforcementTypes),
  status: z.enum(configurationStatuses),
  inheritance: z.enum(inheritanceStates),
  hasWarnings: z.boolean(),
  version: z.number().int().positive(),
  publishedVersion: z.number().int().positive().nullable(),
  isEffective: z.boolean(),
  updatedAt: z.string().datetime(),
  updatedBy: z.string(),
});

export const guardrailDetailSchema = guardrailSummarySchema.extend({
  instruction: z.string(),
  triggerConditions: z.array(z.string()),
  prohibitedBehavior: z.string(),
  requiredBehavior: z.string(),
  fallbackResponse: z.string(),
  escalationRoute: z.string().nullable(),
  policyReference: z.string().nullable(),
  deterministicControl: z.string().nullable(),
  customerExperience: z.string(),
  scope: scopeSchema,
  conflicts: z.array(
    z.object({
      id: z.string().uuid(),
      title: z.string(),
      description: z.string(),
      blocking: z.boolean(),
    }),
  ),
  testSummary: z.object({
    total: z.number().int().nonnegative(),
    passed: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    warnings: z.number().int().nonnegative(),
    lastRunAt: z.string().datetime().nullable(),
  }),
});

export const updateGuardrailSchema = z.object({
  objective: z.string().min(10).max(500),
  severity: z.enum(guardrailSeverities),
  enforcement: z.enum(enforcementTypes),
  instruction: z.string().min(20).max(4000),
  triggerConditions: z.array(z.string().min(1)).max(20),
  prohibitedBehavior: z.string().min(1).max(2000),
  requiredBehavior: z.string().min(1).max(2000),
  fallbackResponse: z.string().min(1).max(2000),
  escalationRoute: z.string().max(250).nullable(),
  changeReason: z.string().min(10).max(500),
  expectedVersion: z.number().int().positive(),
});

export const createGuardrailSchema = updateGuardrailSchema.omit({ expectedVersion: true }).extend({
  name: z.string().min(3).max(150),
  category: z.enum(guardrailCategories),
  scopeId: z.string().uuid(),
});

export const governanceActionSchema = z.object({
  expectedVersion: z.number().int().positive(),
  reason: z.string().min(10).max(500),
});

export const guardrailTestResultSchema = z.object({
  id: z.string().uuid(),
  guardrailId: z.string().uuid(),
  version: z.number().int().positive(),
  total: z.number().int().nonnegative(),
  passed: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  warnings: z.number().int().nonnegative(),
  createdAt: z.string().datetime(),
});

export const paginatedGuardrailsSchema = z.object({
  items: z.array(guardrailSummarySchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});

const idSchema = z.string().uuid();
const dateTimeSchema = z.string().datetime();
const boundedText = (max = 4000) => z.string().min(1).max(max);
export const issueSeverities = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'] as const;
export const reviewStatuses = ['OPEN', 'IN_REVIEW', 'RESOLVED', 'DISMISSED'] as const;

export const knowledgePassageSchema = z.object({
  id: idSchema,
  documentId: idSchema,
  section: z.string(),
  content: z.string(),
  effectiveAt: dateTimeSchema,
  relevance: z.number().min(0).max(1),
  relatedPassageIds: z.array(idSchema),
  exampleQuestions: z.array(z.string()),
  contradictionIds: z.array(idSchema),
});

export const knowledgeDocumentSchema = z.object({
  id: idSchema,
  name: z.string(),
  version: z.string(),
  owner: z.string(),
  effectiveAt: dateTimeSchema,
  updatedAt: dateTimeSchema,
  market: z.string(),
  language: z.string(),
  status: z.enum(configurationStatuses),
  sourceSystem: z.string(),
  useCases: z.array(z.string()),
  readOnly: z.literal(true),
  passages: z.array(knowledgePassageSchema),
});

export const knowledgeBaseSchema = z.object({
  documents: z.array(knowledgeDocumentSchema),
  reviewFlags: z.array(
    z.object({
      id: idSchema,
      passageId: idSchema,
      status: z.enum(reviewStatuses),
      assignee: z.string(),
      explanation: z.string(),
      createdAt: dateTimeSchema,
    }),
  ),
  contradictions: z.array(
    z.object({
      id: idSchema,
      passageIds: z.array(idSchema).min(2),
      severity: z.enum(issueSeverities),
      summary: z.string(),
    }),
  ),
});

export const createReviewFlagSchema = z.object({
  assignee: z.string().min(2).max(150),
  explanation: z.string().min(10).max(2000),
});

export const knowledgeAnalysisSchema = z.object({
  id: idSchema,
  scopeId: idSchema,
  healthScore: z.number().int().min(0).max(100),
  summary: z.string(),
  findings: z.array(
    z.object({
      type: z.enum([
        'CONTRADICTION',
        'MISSING_TOPIC',
        'DUPLICATE',
        'OUTDATED',
        'INCONSISTENCY',
        'UNSUPPORTED_CLAIM',
        'AMBIGUITY',
        'RETRIEVAL_QUALITY',
      ]),
      severity: z.enum(issueSeverities),
      passageIds: z.array(idSchema),
      remediation: z.string(),
      confidence: z.number().min(0).max(1),
    }),
  ),
  createdAt: dateTimeSchema,
});

export const businessRuleSummarySchema = z.object({
  id: idSchema,
  name: z.string(),
  description: z.string(),
  status: z.enum(configurationStatuses),
  priority: z.enum(issueSeverities),
  effectiveFrom: dateTimeSchema.nullable(),
  effectiveTo: dateTimeSchema.nullable(),
  markets: z.array(z.string()),
  channels: z.array(z.string()),
  owner: z.string(),
  version: z.number().int().positive(),
  publishedVersion: z.number().int().positive().nullable(),
  conflictCount: z.number().int().nonnegative(),
  updatedAt: dateTimeSchema,
});

export const businessRuleDetailSchema = businessRuleSummarySchema.extend({
  scopeId: idSchema,
  conditions: z.array(z.string()),
  action: z.string(),
  exceptions: z.array(z.string()),
  requiredData: z.array(z.string()),
  dependencies: z.array(z.string()),
  sourceReference: z.string().nullable(),
  approver: z.string(),
  implementationInstruction: z.string(),
  deploymentPayload: z.record(z.string(), z.unknown()),
  versions: z.array(
    z.object({
      version: z.number().int().positive(),
      status: z.enum(configurationStatuses),
      author: z.string(),
      reason: z.string(),
      createdAt: dateTimeSchema,
    }),
  ),
  conflicts: z.array(
    z.object({ id: idSchema, title: z.string(), description: z.string(), blocking: z.boolean() }),
  ),
});

export const updateBusinessRuleSchema = z.object({
  description: boundedText(500),
  priority: z.enum(issueSeverities),
  conditions: z.array(boundedText(500)).min(1).max(30),
  action: boundedText(2000),
  exceptions: z.array(boundedText(500)).max(20),
  requiredData: z.array(z.string().min(1).max(100)).max(30),
  dependencies: z.array(z.string().min(1).max(150)).max(30),
  implementationInstruction: boundedText(4000),
  changeReason: z.string().min(10).max(500),
  expectedVersion: z.number().int().positive(),
});

export const flowNodeTypes = [
  'START',
  'MESSAGE',
  'COLLECT_DATA',
  'VERIFY',
  'SYSTEM_ACTION',
  'RULE_DECISION',
  'HANDOVER',
  'RECOVERY',
  'END',
] as const;
export const flowNodeSchema = z.object({
  id: z.string().min(1).max(100),
  type: z.enum(flowNodeTypes),
  label: z.string().min(1).max(150),
  description: z.string().max(1000),
  position: z.object({ x: z.number(), y: z.number() }),
  config: z.record(z.string(), z.unknown()),
});
export const flowEdgeSchema = z.object({
  id: z.string().min(1).max(100),
  source: z.string().min(1),
  target: z.string().min(1),
  label: z.string().max(150).nullable(),
  condition: z.string().max(1000).nullable(),
});
const flowSlugSchema = z.string().regex(/^[a-z][a-z0-9_]{0,63}$/);
export const flowRoutingSchema = z.object({
  acknowledgment_required: z.boolean(),
  acknowledgment_description: z.string().min(1).max(500),
  transition_description_template: z.string().min(1).max(500),
  continuation_directive: z.string().min(1).max(1000),
  continuation_directive_no_filler: z.string().min(1).max(1000),
}).strict();
export const flowStateFieldSchema = z.object({
  name: flowSlugSchema,
  type: z.enum(['string', 'number', 'boolean']),
  description: z.string().min(1).max(500),
  enum: z.array(z.string().min(1).max(150)).min(1).optional(),
}).strict();
export const flowTransitionSchema = z.object({
  to: flowSlugSchema,
  when: z.string().min(1).max(1000),
}).strict();
const flowModelSchema = z.object({
  provider: z.enum(['openai', 'google', 'anthropic']),
  name: z.string().min(1).max(150),
  settings: z.record(z.string(), z.unknown()).optional(),
}).strict();
export const flowLlmStepSchema = z.object({
  id: flowSlugSchema,
  type: z.literal('llm'),
  prompt: z.string().min(1).max(12000),
  model: flowModelSchema.optional(),
  routing: flowRoutingSchema.partial().optional(),
  tools: z.array(flowSlugSchema).max(16).default([]),
  collect: z.array(flowSlugSchema).default([]),
  transitions: z.array(flowTransitionSchema).max(8).default([]),
}).strict();
export const flowEndStepSchema = z.object({
  id: flowSlugSchema,
  type: z.literal('end'),
  final_message: z.string().max(4000).nullable().optional(),
  terminate_call: z.boolean().default(true),
}).strict();
export const flowGlobalLlmStepSchema = flowLlmStepSchema.extend({
  when: z.string().min(1).max(1000),
  exclude: z.array(flowSlugSchema).default([]),
});
export const flowGlobalEndStepSchema = flowEndStepSchema.extend({
  when: z.string().min(1).max(1000),
  exclude: z.array(flowSlugSchema).default([]),
});
const flowHttpToolSchema = z.object({
  name: flowSlugSchema,
  kind: z.literal('http'),
  description: z.string().min(1).max(1000),
  parameters: z.record(z.string(), z.unknown()).optional(),
  param_mapping: z.record(z.string(), z.string()).optional(),
  pre_execution_speech: z.string().max(500).optional(),
  pre_execution_speech_allow_interruption: z.boolean().optional(),
  request: z.object({
    method: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']),
    url: z.string().url(),
    headers: z.record(z.string(), z.string()).optional(),
    body: z.record(z.string(), z.unknown()).optional(),
    timeout_sec: z.number().min(1).max(30).optional(),
  }).strict(),
}).strict();
const flowAgentToolSchema = z.object({
  name: flowSlugSchema,
  kind: z.literal('agent_tool'),
  ref: z.string().min(1).max(150),
}).strict();
export const conversationFlowRuntimeSchema = z.object({
  schema_version: z.literal(1),
  entry_step: flowSlugSchema,
  routing: flowRoutingSchema,
  shared_prompt: z.string().max(12000).optional(),
  state_fields: z.array(flowStateFieldSchema).default([]),
  steps: z.array(z.union([flowLlmStepSchema, flowEndStepSchema])).min(1).max(250),
  global_steps: z.array(z.union([flowGlobalLlmStepSchema, flowGlobalEndStepSchema])).default([]),
  tools: z.array(z.union([flowHttpToolSchema, flowAgentToolSchema])).default([]),
}).strict().superRefine((flow, context) => {
  const allSteps = [...flow.steps, ...flow.global_steps];
  const ids = allSteps.map((step) => step.id);
  const idSet = new Set(ids);
  const toolSet = new Set(flow.tools.map((tool) => tool.name));
  const fieldSet = new Set(flow.state_fields.map((field) => field.name));
  const add = (message: string, path: Array<string | number>) => context.addIssue({ code: 'custom', message, path });
  if (ids.length !== idSet.size) add('Step ids must be unique across steps and global_steps.', ['steps']);
  const entry = flow.steps.find((step) => step.id === flow.entry_step);
  if (!entry || entry.type !== 'llm') add('entry_step must reference a regular llm step.', ['entry_step']);
  for (const [index, field] of flow.state_fields.entries()) if (field.enum && field.type !== 'string') add('enum is only valid for string state fields.', ['state_fields', index, 'enum']);
  for (const [index, step] of allSteps.entries()) {
    if (step.type !== 'llm') continue;
    for (const transition of step.transitions) if (!idSet.has(transition.to)) add(`Unknown transition target ${transition.to}.`, ['steps', index, 'transitions']);
    for (const tool of step.tools) if (!toolSet.has(tool)) add(`Unknown tool ${tool}.`, ['steps', index, 'tools']);
    for (const field of step.collect) if (!fieldSet.has(field)) add(`Unknown state field ${field}.`, ['steps', index, 'collect']);
  }
  const llmIds = new Set(allSteps.filter((step) => step.type === 'llm').map((step) => step.id));
  for (const [index, step] of flow.global_steps.entries()) for (const excluded of step.exclude) if (!llmIds.has(excluded)) add(`Global exclusion ${excluded} must reference an llm step.`, ['global_steps', index, 'exclude']);
  const template = flow.routing.transition_description_template;
  if ((template.match(/\{when\}/g) ?? []).length !== 1 || /[{}]/.test(template.replace('{when}', ''))) add('Must contain exactly one {when} placeholder and no others.', ['routing', 'transition_description_template']);
});
export const flowLayoutSchema = z.record(
  flowSlugSchema,
  z.object({ x: z.number(), y: z.number() }),
);
export const conversationFlowSchema = z.object({
  id: idSchema,
  scopeId: idSchema,
  name: z.string(),
  version: z.number().int().positive(),
  publishedVersion: z.number().int().positive().nullable(),
  status: z.enum(configurationStatuses),
  workflow: conversationFlowRuntimeSchema,
  layout: flowLayoutSchema,
  updatedAt: dateTimeSchema,
  updatedBy: z.string(),
});
export const updateConversationFlowSchema = z.object({
  workflow: conversationFlowRuntimeSchema,
  layout: flowLayoutSchema,
  changeReason: z.string().min(10).max(500),
  expectedVersion: z.number().int().positive(),
});
export const flowValidationSchema = z.object({
  id: idSchema,
  flowId: idSchema,
  version: z.number().int().positive(),
  valid: z.boolean(),
  findings: z.array(
    z.object({
      code: z.enum([
        'LOOP',
        'DEAD_END',
        'UNREACHABLE',
        'MISSING_ERROR_HANDLING',
        'MISSING_HANDOVER',
        'REPEATED_QUESTION',
        'UNAVAILABLE_DATA',
        'GUARDRAIL_CONFLICT',
        'MISSING_EXIT',
        'RUNTIME_SCHEMA',
        'INVALID_EDGE',
      ]),
      severity: z.enum(issueSeverities),
      nodeIds: z.array(z.string()),
      message: z.string(),
    }),
  ),
  createdAt: dateTimeSchema,
});
export const simulateFlowSchema = z.object({
  message: z.string().min(1).max(2000),
  persona: z.string().max(100).optional(),
  branch: z.string().max(100).optional(),
  expectedVersion: z.number().int().positive(),
});
export const flowSimulationSchema = z.object({
  id: idSchema,
  flowId: idSchema,
  version: z.number().int().positive(),
  transcript: z.array(
    z.object({ speaker: z.enum(['CUSTOMER', 'ASSISTANT', 'SYSTEM']), message: z.string() }),
  ),
  decisionTrace: z.array(
    z.object({
      nodeId: z.string(),
      decision: z.string(),
      rule: z.string().nullable(),
      guardrails: z.array(z.string()),
      durationMs: z.number().int().nonnegative(),
    }),
  ),
  collectedData: z.record(z.string(), z.unknown()),
  createdAt: dateTimeSchema,
});

export const phraseGroupsSchema = z.object({
  core: z.array(z.string()),
  keywords: z.array(z.string()),
  synonyms: z.array(z.string()),
  misspellings: z.array(z.string()),
  localTerminology: z.array(z.string()),
  multilingual: z.array(z.string()),
  exclusions: z.array(z.string()),
  contextualIndicators: z.array(z.string()),
  requiredEntities: z.array(z.string()),
});
export const intentWordingSchema = z.object({
  id: idSchema,
  scopeId: idSchema,
  name: z.string(),
  description: z.string(),
  version: z.number().int().positive(),
  publishedVersion: z.number().int().positive().nullable(),
  status: z.enum(configurationStatuses),
  phrases: phraseGroupsSchema,
  competingIntents: z.array(
    z.object({
      name: z.string(),
      description: z.string(),
      signals: z.array(z.string()),
      overlapRate: z.number().min(0).max(1),
    }),
  ),
  updatedAt: dateTimeSchema,
  updatedBy: z.string(),
});
export const updateIntentWordingSchema = z.object({
  phrases: phraseGroupsSchema,
  description: z.string().min(10).max(1000),
  changeReason: z.string().min(10).max(500),
  expectedVersion: z.number().int().positive(),
});
export const testPhraseSchema = z.object({
  message: z.string().min(1).max(1000),
  expectedVersion: z.number().int().positive(),
});
export const phraseTestResultSchema = z.object({
  detectedIntent: z.string(),
  confidence: z.number().min(0).max(1),
  alternatives: z.array(z.object({ intent: z.string(), confidence: z.number().min(0).max(1) })),
  triggeringLanguage: z.array(z.string()),
  entities: z.array(z.object({ type: z.string(), value: z.string() })),
  selectedUseCase: z.string(),
  clarification: z.object({ required: z.boolean(), message: z.string() }),
});

export const analyticsQuerySchema = z.object({
  scopeId: idSchema,
  from: z.string().date(),
  to: z.string().date(),
  market: z.string().max(10).optional(),
  channel: z.string().max(50).optional(),
  language: z.string().max(20).optional(),
});
export const analyticsDashboardSchema = z.object({
  scopeId: idSchema,
  from: z.string().date(),
  to: z.string().date(),
  metrics: z.record(z.string(), z.number()),
  trend: z.array(
    z.object({
      date: z.string().date(),
      conversations: z.number().int().nonnegative(),
      containmentRate: z.number(),
      handoverRate: z.number(),
      positive: z.number(),
      neutral: z.number(),
      negative: z.number(),
    }),
  ),
  entryIntents: z.array(z.object({ name: z.string(), count: z.number().int().nonnegative() })),
  handoverReasons: z.array(
    z.object({ reason: z.string(), percentage: z.number().min(0).max(100) }),
  ),
  funnel: z.array(
    z.object({
      stage: z.string(),
      count: z.number().int().nonnegative(),
      percentage: z.number().min(0).max(100),
    }),
  ),
  failurePoints: z.array(z.object({ name: z.string(), count: z.number().int().nonnegative() })),
  recommendations: z.array(
    z.object({
      id: idSchema,
      evidence: z.string(),
      inference: z.string(),
      action: z.string(),
      expectedImpact: z.string(),
      confidence: z.number().min(0).max(1),
      requiresApproval: z.literal(true),
    }),
  ),
  generatedAt: dateTimeSchema,
});

export const apiErrorSchema = z.object({
  statusCode: z.number().int(),
  code: z.string(),
  message: z.string(),
  correlationId: z.string(),
  details: z.unknown().optional(),
});

export const agentKinds = ['ORCHESTRATOR', 'AGENT', 'SUB_AGENT'] as const;
export const agentStatuses = ['ACTIVE', 'RETIRED'] as const;
export const factTypes = ['STRING', 'INTEGER', 'BOOLEAN', 'DATETIME', 'ENUM', 'LIST', 'OBJECT'] as const;
export const agentChannels = ['web', 'whatsapp', 'ivr'] as const;

const catalogKeySchema = z
  .string()
  .min(1)
  .max(150)
  .regex(/^[a-z0-9_]+(\.[a-z0-9_]+)*$/, 'Use lower-case dot-separated keys, for example servicing.flight_status.');

export const agentActionSchema = z.object({
  key: z.string().min(1).max(150),
  via: z.string().min(1).max(100),
});

export const catalogAgentSchema = z.object({
  key: catalogKeySchema,
  name: boundedText(150),
  kind: z.enum(agentKinds),
  runtime: z.string().min(1).max(100),
  repository: z.string().min(1).max(200),
  implementation: z.string().min(1).max(500),
  parentKey: catalogKeySchema.nullable(),
  channels: z.array(z.enum(agentChannels)).max(agentChannels.length),
  intents: z.array(z.string().min(1).max(150)).max(50),
  prompts: z.array(z.string().min(1).max(300)).max(50),
  actions: z.array(agentActionSchema).max(200),
  description: boundedText(4000),
});

export const catalogFactSchema = z.object({
  key: catalogKeySchema,
  type: z.enum(factTypes),
  source: boundedText(1000),
  description: boundedText(2000),
});

export const agentCatalogSourceSchema = z.object({
  name: boundedText(200),
  generatedAt: dateTimeSchema,
  repositories: z
    .array(
      z.object({
        repository: z.string().min(1).max(200),
        commit: z.string().regex(/^[0-9a-f]{7,40}$/),
        commitDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      }),
    )
    .min(1)
    .max(50),
});

export const agentCatalogSchema = z
  .object({
    schemaVersion: z.literal(1),
    source: agentCatalogSourceSchema,
    agents: z.array(catalogAgentSchema).min(1).max(1000),
    facts: z.array(catalogFactSchema).max(2000),
  })
  .superRefine((catalog, context) => {
    const keys = new Set<string>();
    catalog.agents.forEach((agent, index) => {
      if (keys.has(agent.key)) {
        context.addIssue({ code: 'custom', path: ['agents', index, 'key'], message: `Duplicate agent key ${agent.key}.` });
      }
      keys.add(agent.key);
    });
    catalog.agents.forEach((agent, index) => {
      if (agent.parentKey === agent.key || (agent.parentKey !== null && !keys.has(agent.parentKey))) {
        context.addIssue({ code: 'custom', path: ['agents', index, 'parentKey'], message: `Unknown parent agent ${agent.parentKey}.` });
      }
    });
    const facts = new Set<string>();
    catalog.facts.forEach((fact, index) => {
      if (facts.has(fact.key)) {
        context.addIssue({ code: 'custom', path: ['facts', index, 'key'], message: `Duplicate fact key ${fact.key}.` });
      }
      facts.add(fact.key);
    });
  });

export const agentSummarySchema = catalogAgentSchema
  .pick({ key: true, name: true, kind: true, runtime: true, parentKey: true, channels: true })
  .extend({
    id: idSchema,
    status: z.enum(agentStatuses),
    actionCount: z.number().int().nonnegative(),
    updatedAt: dateTimeSchema,
  });

export const agentDetailSchema = catalogAgentSchema.extend({
  id: idSchema,
  status: z.enum(agentStatuses),
  children: z.array(z.object({ key: catalogKeySchema, name: z.string() })),
  updatedAt: dateTimeSchema,
});

export const agentFactSchema = catalogFactSchema.extend({
  id: idSchema,
  status: z.enum(agentStatuses),
});

export const agentCatalogImportSchema = z.object({
  id: idSchema,
  sourceName: z.string(),
  generatedAt: dateTimeSchema,
  repositories: agentCatalogSourceSchema.shape.repositories,
  contentHash: z.string(),
  importedBy: z.string(),
  importedAt: dateTimeSchema,
  created: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
  retired: z.number().int().nonnegative(),
  unchanged: z.number().int().nonnegative(),
});

export const agentRegistrySchema = z.object({
  agents: z.array(agentSummarySchema),
  facts: z.array(agentFactSchema),
  lastImport: agentCatalogImportSchema.nullable(),
});

export type Scope = z.infer<typeof scopeSchema>;
export type GuardrailSummary = z.infer<typeof guardrailSummarySchema>;
export type GuardrailDetail = z.infer<typeof guardrailDetailSchema>;
export type UpdateGuardrailInput = z.infer<typeof updateGuardrailSchema>;
export type CreateGuardrailInput = z.infer<typeof createGuardrailSchema>;
export type GovernanceActionInput = z.infer<typeof governanceActionSchema>;
export type KnowledgeBase = z.infer<typeof knowledgeBaseSchema>;
export type CreateReviewFlagInput = z.infer<typeof createReviewFlagSchema>;
export type KnowledgeAnalysis = z.infer<typeof knowledgeAnalysisSchema>;
export type BusinessRuleSummary = z.infer<typeof businessRuleSummarySchema>;
export type BusinessRuleDetail = z.infer<typeof businessRuleDetailSchema>;
export type UpdateBusinessRuleInput = z.infer<typeof updateBusinessRuleSchema>;
export type ConversationFlow = z.infer<typeof conversationFlowSchema>;
export type UpdateConversationFlowInput = z.infer<typeof updateConversationFlowSchema>;
export type ConversationFlowRuntime = z.infer<typeof conversationFlowRuntimeSchema>;
export type FlowValidation = z.infer<typeof flowValidationSchema>;
export type SimulateFlowInput = z.infer<typeof simulateFlowSchema>;
export type FlowSimulation = z.infer<typeof flowSimulationSchema>;
export type IntentWording = z.infer<typeof intentWordingSchema>;
export type UpdateIntentWordingInput = z.infer<typeof updateIntentWordingSchema>;
export type TestPhraseInput = z.infer<typeof testPhraseSchema>;
export type PhraseTestResult = z.infer<typeof phraseTestResultSchema>;
export type AnalyticsQuery = z.infer<typeof analyticsQuerySchema>;
export type AnalyticsDashboard = z.infer<typeof analyticsDashboardSchema>;
export type PaginatedGuardrails = z.infer<typeof paginatedGuardrailsSchema>;
export type ApiError = z.infer<typeof apiErrorSchema>;
export type AgentAction = z.infer<typeof agentActionSchema>;
export type CatalogAgent = z.infer<typeof catalogAgentSchema>;
export type CatalogFact = z.infer<typeof catalogFactSchema>;
export type AgentCatalog = z.infer<typeof agentCatalogSchema>;
export type AgentSummary = z.infer<typeof agentSummarySchema>;
export type AgentDetail = z.infer<typeof agentDetailSchema>;
export type AgentFact = z.infer<typeof agentFactSchema>;
export type AgentCatalogImport = z.infer<typeof agentCatalogImportSchema>;
export type AgentRegistry = z.infer<typeof agentRegistrySchema>;
